import type { RunState } from "@iqa/contracts";
import { RUN_STATE_FIELDS } from "@iqa/contracts/live-fields";
import { useEffect, useRef, useState } from "react";

/**
 * Runs' live state as it changes (docs/carrier-runs.md#live-state): the data API's subscriptions
 * over AppSync's real-time WebSocket, and polling when they fail. Every bound is fixed: a
 * subscription that does not start within CONNECT_MS, or stops, is tried again at most
 * MAX_FAILURES times in a row and MAX_ATTEMPTS times per page; meanwhile and after, the page polls
 * every POLL_MS.
 */

export type { RunState };

/** One run, or every run (admins and operators). */
export type LiveTarget = { readonly runId: string } | "all";

export interface LiveHandlers {
  /** A state reported; it may be older than one already shown (compare revisions). */
  onState(state: RunState): void;
  /** The subscription started. */
  onReady(): void;
  /** It could not start, or stopped; nothing more comes from it. */
  onFail(reason: string): void;
}

/** Subscribes to live state; returns the unsubscribe. Tests give a fake one. */
export interface LiveFeed {
  subscribe(target: LiveTarget, handlers: LiveHandlers): () => void;
}

/** A subscription that does not start within this fails. */
export const CONNECT_MS = 10_000;
/** How often a page polls while it has no subscription. */
export const POLL_MS = 15_000;
/** Consecutive failures before the page keeps to polling. */
export const MAX_FAILURES = 3;
/** Subscriptions a page makes at most, reconnects included. */
export const MAX_ATTEMPTS = 10;
/** The waits before trying a subscription again, by failures in a row. */
export const RETRY_MS: readonly number[] = [2_000, 10_000];
/** A run active with no activity for this long has probably lost its machine (idle is 15 min). */
export const STALE_MS = 16 * 60_000;

/** A feed that never connects: the local data API serves no subscriptions. */
export function pollingOnly(reason: string): LiveFeed {
  return {
    subscribe: (_target, handlers) => {
      const timer = setTimeout(() => handlers.onFail(reason), 0);
      return () => clearTimeout(timer);
    },
  };
}

/**
 * AppSync's real-time endpoint for its GraphQL endpoint: `https://<id>.appsync-api.<region>…`
 * becomes `wss://<id>.appsync-realtime-api.<region>…`. Undefined for anything else.
 */
export function realtimeUrl(apiUrl: string): string | undefined {
  let url: URL;
  try {
    url = new URL(apiUrl);
  } catch {
    return undefined;
  }
  if (url.protocol !== "https:" || !/\.appsync-api\./.test(url.hostname)) return undefined;
  return `wss://${url.hostname.replace(".appsync-api.", ".appsync-realtime-api.")}${url.pathname}`;
}

/** GraphQL's `null`s left out, as the contracts' types have it. */
function withoutNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutNulls);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, field]) => field !== null)
      .map(([name, field]) => [name, withoutNulls(field)]),
  );
}

/** The subscriptions the portal makes. */
export const SUBSCRIPTIONS = {
  one: `subscription OnRunState($runId: ID!) { onRunState(runId: $runId) { ${RUN_STATE_FIELDS} } }`,
  all: `subscription OnRunStates { onRunStates { ${RUN_STATE_FIELDS} } }`,
};

type Message = { type?: string; id?: string; payload?: Record<string, unknown> };

export interface AppSyncFeedOptions {
  readonly apiUrl: string;
  readonly token: () => Promise<string | undefined>;
  /** For tests. */
  readonly WebSocket?: typeof WebSocket;
  readonly connectMs?: number;
}

/**
 * The data API's subscriptions over AppSync's real-time protocol: one WebSocket per subscription,
 * authorised with the user's id token (AppSync's `graphql-ws` subprotocol: connection_init →
 * connection_ack, start → start_ack, then data and keep-alives). A missing keep-alive, an error or a
 * close fails it. Undefined when the API is not AppSync's (the local data API).
 */
export function appSyncFeed(options: AppSyncFeedOptions): LiveFeed | undefined {
  const endpoint = realtimeUrl(options.apiUrl);
  if (!endpoint) return undefined;
  const host = new URL(options.apiUrl).host;
  const Socket = options.WebSocket ?? globalThis.WebSocket;
  const connectMs = options.connectMs ?? CONNECT_MS;
  return {
    subscribe(target, handlers) {
      let done = false;
      let socket: WebSocket | undefined;
      let connectTimer: ReturnType<typeof setTimeout> | undefined;
      let aliveTimer: ReturnType<typeof setTimeout> | undefined;
      /** AppSync's keep-alive bound; 5 minutes until its connection_ack says. */
      let quietMs = 5 * 60_000;
      const id = crypto.randomUUID();
      const stop = () => {
        done = true;
        clearTimeout(connectTimer);
        clearTimeout(aliveTimer);
        if (socket) {
          socket.onmessage = socket.onerror = socket.onclose = socket.onopen = null;
          try {
            if (socket.readyState === 1) socket.send(JSON.stringify({ type: "stop", id }));
            socket.close();
          } catch {
            // Closing anyway.
          }
        }
      };
      const fail = (reason: string) => {
        if (done) return;
        stop();
        handlers.onFail(reason);
      };
      const alive = (ms: number) => {
        clearTimeout(aliveTimer);
        aliveTimer = setTimeout(() => fail("the live connection went quiet"), ms);
      };
      connectTimer = setTimeout(() => fail("the live connection did not start"), connectMs);
      void (async () => {
        const token = await options.token().catch(() => undefined);
        if (done) return;
        if (!token) return fail("signed out");
        const header = btoa(JSON.stringify({ host, Authorization: token }));
        try {
          socket = new Socket(`${endpoint}?header=${encodeURIComponent(header)}&payload=e30=`, [
            "graphql-ws",
          ]);
        } catch (error) {
          return fail(error instanceof Error ? error.message : String(error));
        }
        const field = target === "all" ? "onRunStates" : "onRunState";
        socket.onopen = () => socket?.send(JSON.stringify({ type: "connection_init" }));
        socket.onerror = () => fail("the live connection failed");
        socket.onclose = () => fail("the live connection closed");
        socket.onmessage = (event: MessageEvent) => {
          let message: Message;
          try {
            message = JSON.parse(String(event.data)) as Message;
          } catch {
            return;
          }
          switch (message.type) {
            case "connection_ack": {
              // AppSync says how long it may go without a keep-alive; every "ka" restarts it.
              const timeout = Number(message.payload?.connectionTimeoutMs);
              if (Number.isFinite(timeout) && timeout > 0) quietMs = timeout;
              alive(quietMs);
              socket?.send(
                JSON.stringify({
                  id,
                  type: "start",
                  payload: {
                    data: JSON.stringify(
                      target === "all"
                        ? { query: SUBSCRIPTIONS.all, variables: {} }
                        : { query: SUBSCRIPTIONS.one, variables: { runId: target.runId } },
                    ),
                    extensions: { authorization: { Authorization: token, host } },
                  },
                }),
              );
              return;
            }
            case "ka":
              alive(quietMs);
              return;
            case "start_ack":
              clearTimeout(connectTimer);
              handlers.onReady();
              return;
            case "data": {
              const data = message.payload?.data as Record<string, unknown> | undefined;
              const state = data?.[field];
              if (state && typeof state === "object")
                handlers.onState(withoutNulls(state) as RunState);
              return;
            }
            case "error":
            case "connection_error": {
              const errors = message.payload?.errors as { message?: string }[] | undefined;
              return fail(errors?.[0]?.message ?? "the live connection was refused");
            }
            case "complete":
              return fail("the live connection ended");
            default:
              return;
          }
        };
      })();
      return stop;
    },
  };
}

/** The time now, moved on every `ms`, so "4 min ago" keeps up. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

/** The newer of two states by revision; the first when they tie. */
export function newer(a: RunState | undefined, b: RunState | undefined): RunState | undefined {
  if (!a) return b;
  if (!b) return a;
  return b.revision > a.revision ? b : a;
}

export type LiveMode = "connecting" | "live" | "polling";

export interface LiveStates {
  /** By run id: the newest state seen. */
  readonly states: ReadonlyMap<string, RunState>;
  readonly mode: LiveMode;
  /** Why it is polling. */
  readonly reason?: string;
}

/**
 * The live state of the runs a page shows: `initial` (what the page loaded), then every newer
 * state the subscription brings; while it has none, `poll` every POLL_MS. Older states are dropped
 * by revision, whatever their order.
 */
export function useLiveStates(
  feed: LiveFeed,
  target: LiveTarget,
  initial: readonly RunState[],
  poll: () => Promise<readonly RunState[]>,
): LiveStates {
  const [states, setStates] = useState<ReadonlyMap<string, RunState>>(new Map());
  const [mode, setMode] = useState<LiveMode>("connecting");
  const [reason, setReason] = useState<string>();
  const pollRef = useRef(poll);
  pollRef.current = poll;
  const key = target === "all" ? "all" : target.runId;

  const merge = (incoming: readonly RunState[]) =>
    setStates((current) => {
      let next: Map<string, RunState> | undefined;
      for (const state of incoming) {
        const kept = current.get(state.runId);
        if (newer(kept, state) === kept && kept) continue;
        next ??= new Map(current);
        next.set(state.runId, state);
      }
      return next ?? current;
    });

  // What the page loaded counts as seen; a later reload merges the same way.
  useEffect(() => merge(initial), [initial]);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let polling: ReturnType<typeof setInterval> | undefined;
    let attempts = 0;
    let failures = 0;
    const pollOnce = () => {
      pollRef.current().then(
        (found) => active && merge(found),
        () => {},
      );
    };
    const startPolling = () => {
      if (polling) return;
      pollOnce();
      polling = setInterval(pollOnce, POLL_MS);
    };
    const subscribe = () => {
      attempts++;
      unsubscribe = feed.subscribe(target, {
        onState: (state) => active && merge([state]),
        onReady: () => {
          if (!active) return;
          failures = 0;
          // What changed while it was polling or reconnecting is read once.
          if (polling) pollOnce();
          clearInterval(polling);
          polling = undefined;
          setMode("live");
          setReason(undefined);
        },
        onFail: (why) => {
          if (!active) return;
          failures++;
          setMode("polling");
          setReason(why);
          startPolling();
          if (failures < MAX_FAILURES && attempts < MAX_ATTEMPTS)
            retry = setTimeout(subscribe, RETRY_MS[failures - 1] ?? RETRY_MS.at(-1));
        },
      });
    };
    subscribe();
    return () => {
      active = false;
      unsubscribe?.();
      clearTimeout(retry);
      clearInterval(polling);
    };
    // The target is named by its key.
  }, [feed, key]);

  return { states, mode, ...(reason ? { reason } : {}) };
}
